/**
 * Shared supply/toner color configuration
 * Used by both device-detail.tsx and device-table-field.tsx
 */

export interface SupplyColorConfig {
  key: string;           // lowercase key used in data (e.g., 'matteblack')
  label: string;         // Display label (e.g., 'Matte Black')
  barColor: string;      // CSS color/gradient for progress bar
  fieldSuffix: string;   // PascalCase suffix for field names (e.g., 'MatteBlack')
  isExtended: boolean;   // Whether this is an extended color (not standard CMYK)
}

// All supply colors in display order per AC requirements
export const SUPPLY_COLORS: SupplyColorConfig[] = [
  // Black variants
  { key: 'black', label: 'Black', barColor: '#000000', fieldSuffix: 'Black', isExtended: false },
  { key: 'matteblack', label: 'Matte Black', barColor: '#1a1a1a', fieldSuffix: 'MatteBlack', isExtended: true },
  { key: 'photoblack', label: 'Photo Black', barColor: '#2d2d2d', fieldSuffix: 'PhotoBlack', isExtended: true },
  // Cyan variants
  { key: 'cyan', label: 'Cyan', barColor: '#00bcd4', fieldSuffix: 'Cyan', isExtended: false },
  { key: 'photocyan', label: 'Photo Cyan', barColor: '#4dd0e1', fieldSuffix: 'PhotoCyan', isExtended: true },
  // Magenta variants
  { key: 'magenta', label: 'Magenta', barColor: '#e91e63', fieldSuffix: 'Magenta', isExtended: false },
  { key: 'photomagenta', label: 'Photo Magenta', barColor: '#f48fb1', fieldSuffix: 'PhotoMagenta', isExtended: true },
  // Yellow
  { key: 'yellow', label: 'Yellow', barColor: '#ffeb3b', fieldSuffix: 'Yellow', isExtended: false },
  // Other colors
  { key: 'blue', label: 'Blue', barColor: '#2196f3', fieldSuffix: 'Blue', isExtended: true },
  { key: 'red', label: 'Red', barColor: '#f44336', fieldSuffix: 'Red', isExtended: true },
  // Gray variants
  { key: 'gray', label: 'Gray', barColor: '#9e9e9e', fieldSuffix: 'Gray', isExtended: true },
  { key: 'photogray', label: 'Photo Gray', barColor: '#bdbdbd', fieldSuffix: 'PhotoGray', isExtended: true },
  // Special
  { key: 'chromaoptimizer', label: 'Chroma Optimizer', barColor: 'linear-gradient(90deg, #ff6b6b, #feca57, #48dbfb, #ff9ff3, #54a0ff)', fieldSuffix: 'ChromaOptimizer', isExtended: true },
  { key: 'maintenance', label: 'Maintenance', barColor: '#424242', fieldSuffix: 'Maintenance', isExtended: true },
];

// Quick lookup maps
export const STANDARD_COLORS = SUPPLY_COLORS.filter(c => !c.isExtended);
export const EXTENDED_COLORS = SUPPLY_COLORS.filter(c => c.isExtended);

// Get color config by key
export const getColorConfig = (key: string): SupplyColorConfig | undefined => 
  SUPPLY_COLORS.find(c => c.key === key.toLowerCase());

// Get all color keys
export const ALL_COLOR_KEYS = SUPPLY_COLORS.map(c => c.key);
export const STANDARD_COLOR_KEYS = STANDARD_COLORS.map(c => c.key);
export const EXTENDED_COLOR_KEYS = EXTENDED_COLORS.map(c => c.key);

// Field name helpers
export const getLevelField = (suffix: string) => `level${suffix}`;
export const getLastOrderDateField = (suffix: string) => `LastOrderDate${suffix}`;
export const getOrderStatusField = (suffix: string) => `OrderStatus${suffix}`;
export const getTrackingField = (suffix: string) => `TrackingNumbers${suffix}`;
export const getLastReplaceField = (suffix: string) => `LastReplace${suffix}`;
export const getSOIDField = (suffix: string) => `SOID${suffix}`;
export const getAuditDateField = (suffix: string) => `Auditdate${suffix}`;

